import { defineStore } from "pinia";
import PagesServices from "@/services/PagesServices";
import AppsServices from "@/services/AppsServices";
import FieldsServices from "@/services/FieldsServices";
import RolesServices from "@/services/RolesServices";
import CollectionsServices from "@/services/CollectionsServices";
import DatasEditorServices from "@/services/DatasEditorServices";
import debounce from "lodash.debounce";
import functions from "@/util/Functions";

import { parseJsonField } from '@/util/parseJson'

function parseMeta(meta) {
  if (!meta) return {};
  if (typeof meta === 'string') {
    try {
      return JSON.parse(meta);
    } catch (e) {
      // Foute JSON? Geef leeg object terug
      console.warn("Kon meta niet parsen:", meta, e);
      return {};
    }
  }
  return meta;
}


function normalizePage(page) {
  return {
    ...page,
    content: parseJsonField(page.content),
    data: {
      ...page.data,
      component: {
        ...page.data?.component,
        steps: parseJsonField(page.data?.component?.steps),
      }
    }
  };
}



export const useAppStore = defineStore("appStore", {
  state: () => ({
    activeStepIndex: 1,
    hoverwidget: 0,
    updatingpage: false,
    edit_mode: false,
    developers_info: false,
    app_preview_mode: false,

    newPage: {},
    active: {
      app: {},
      page: {},
      view: {},
      user: {},
      role: {},
      step: {},
      widget: {
        component: {},
        options: {},
        actions: {
          add_item: {},
          edit_item: {},
          delete_item: {},
        }
      },
      collection: {},
      detail_page: {},
      edit_page: {},
      form_screen: {},
    },

    pages: [],
      collections: [],
      users: [],
      roles: [],
      fields: [],
      all_fields: [],
      data: [],
      show_form_screen: false,
      is_saving_page: false,
      loading: false,
      error: null,
      pagination: {},
      navigationStack: [], 
      breadcrumb: [],
  
  }),
  

  actions: {
    reset() {
      // alias – kun je in je componenten aanroepen
      this.$reset()
    },
     /****************************************************
     * APP API and functions
     ****************************************************/
     async getApp() {
      try {
        await this.loadAppData();
        await this.loadInitialAppContext();
      } catch (error) {
        console.error("App laden mislukt:", error);
      }
    },
    
    async loadAppData() {
      const res = await AppsServices.getApp();
      this.app = res.data.data;
    
      if (this.app?.color) {
        functions.generateColorVariants(this.app.color);
      }

      if(this.app?.border_radius){
        functions.generateBorderRadius(this.app?.border_radius);
      }
    },
    
    async loadInitialAppContext() {
      await Promise.all([
        this.getCollections(),
        this.getAllFields(),
        this.getPages(),
        this.getUsers(),
        this.getRoles()
      ]);
    },
    

    // setActiveStep(index) {
    //   if (index >= 0 && index < this.getActivePage?.data?.component?.steps?.length) {
    //     this.activeStepIndex = index;
    //   }
    // },

    async setActiveStep(index) {
      const steps = this.getActivePage?.data?.component?.steps;
      if (!steps || !Array.isArray(steps)) return;
    
      if (index >= 0 && index < steps.length) {
        this.activeStepIndex = index;
    
        const step = steps[index];
        // Zorg voor een deep copy zodat reactiviteit correct werkt
        this.active.step = JSON.parse(JSON.stringify(step));
      }
    },
    
    async setActivePage(page) {
      if (!page) return;
    
      if (page.meta) {
        page.meta = parseMeta(page.meta);
      }
    
      this.active.page = JSON.parse(JSON.stringify(page));      
      sessionStorage.setItem('lastActivePageId', page.id);
    
    },


 



    addStep(stepIndex) {
      if (!this.getActivePage.data?.component?.steps) {
        this.getActivePage.data.component.steps = [];
      }
    
      const steps = this.getActivePage.data.component.steps;
    
      const newStep = {
        screentype: "formstep",
        number: steps.length + 1,
        header: {
          title: `New step`,
          description: "",
        },
        data: {
          fields: [],
        },
      };
      if (typeof stepIndex === 'number' && stepIndex >= 0 && stepIndex < steps.length) {
        steps.splice(stepIndex + 1, 0, newStep);
        this.activeStepIndex = stepIndex + 1;
      } else {
        steps.push(newStep);
        this.activeStepIndex = steps.length - 1;
      }
    
      // Hernummer alle stappen netjes
      steps.forEach((step, index) => {
        step.number = index + 1;
        step.header.title = `Stap ${index + 1}`;
      });
    
      this.updatePage();
    },
    

    setActiveApp(app) {
      this.active.app = app;
    },
    clearActiveApp() {
      this.active.app = {};
    },
    updateActiveAppField(key, value) {
      if (this.active.app) {
        this.active.app[key] = value;
      }
    },



    // Breadcrumbs

    addBreadcrumb(item) {
      const last = this.breadcrumb[this.breadcrumb.length - 1];
      if (!last || last.id !== item.id) {
        this.breadcrumb.push(item);
      }
    },
    
    popBreadcrumb() {
      this.breadcrumb.pop();
    },
    
    clearBreadcrumb() {
      this.breadcrumb = [];
    },
    
    setBreadcrumb(trail) {
      this.breadcrumb = trail;
    },

   
    togglePreview() {
      this.app_preview_mode = !this.app_preview_mode;
      this.edit_mode = !this.app_preview_mode;
    },
    setPreview(v) {
      this.app_preview_mode = v;
      this.edit_mode = !v;
    },
 
    async getPages() {
      try {
        const response = await PagesServices.getPages();
        this.pages = response.data.data.map(page => {
          // parse content altijd naar array
          const content = parseJsonField(page.content);

          // als er een component object is, parse dan ook de steps
          const component = page.data?.component
            ? {
                ...page.data.component,
                steps: parseJsonField(page.data.component.steps)
              }
            : undefined;

          return {
            ...page,
            content,
            data: {
              ...page.data,
              // alleen toevoegen als component bestaat
              ...(component ? { component } : {})
            }
          };
        });

        // zet actieve pagina
        const lastId = sessionStorage.getItem('lastActivePageId');
        const pageToSet =
          this.pages.find(p => String(p.id) === lastId) ||
          this.pages[0];

        if (pageToSet) {
          this.setActivePage(pageToSet);
          this.setActiveView(pageToSet);
        }
      } catch (error) {
        console.error('Fout bij ophalen van pages:', error);
      }
    },

   
      async storePage(data) {
        this.loading = true;
        this.error = null;

        const component = {
          component: {
            title: "",
            description: "",
            meta: "",
            image: "",
          },
          design: {
            size: "",
            list_style: "",
          },
  
          options: {
            search: {
              show_searchbar: 0,
            },
          },
          actions: {
            add_item: {
              active: 1,
              btn_icon: "plus",
              btn_text: "Toevoegen",
            },
            edit_item: {
              active: 1,
              btn_icon: "pencil",
              btn_text: "Bewerken",
            },
            delete_item: {
              active: 1,
              btn_icon: "trash",
              btn_text: "Verwijderen",
            },
          },
        };
  
        const newNav = {
          // id: new_page_id,
          collection: data.collection,
          title: data.title,
          icon: data.icon,
          show_in_menu: data.show_in_menu,
          hide_menu: data.hide_menu,
          login_required: data.login_required,
          screentype: data.screentype,
          widget_style: data.widget_style ? data.widget_style : "list",
          data: component,

          meta: {
            ...(typeof data.meta === 'object' && !Array.isArray(data.meta) ? data.meta : {}),            
          },
          ...this.data,
        };

        try {
          const response = await PagesServices.createPage(newNav);
  
          // Verkrijg de net aangemaakte page uit de API
          const created = response.data.page;
          // parse content en steps
          const normalized = {
            ...created,
            content: parseJsonField(created.content),
            data: {
              ...created.data,
              component: {
                ...created.data.component,
                steps: parseJsonField(created.data.component.steps)
              }
            }
          };
  
          // voeg toe aan begin van lijst
          this.pages.unshift(normalized);
  
          // stel direct actief
          this.setActivePage(normalized);
          this.setActiveView(normalized);
        } catch (err) {
          console.error('Er is een fout opgetreden bij opslaan van pagina:', err);
          this.error = 'Opslaan pagina mislukt';
        } finally {
          this.loading = false;
        }
      },
      async duplicatePage(data) {
        this.loading = true;
        this.error   = null;
  
        try {
          // Bouw het payload voor duplicatie
          const duplicatedTitle     = `${data.title} (kopie)`;
          const duplicatedComponent = { 
            ...parseJsonField(data.data), 
            component: {
              ...parseJsonField(data.data.component),
              // pas hier eventueel andere velden aan
            }
          };
  
          const payload = {
            ...data, // behoud alle velden
            title:        duplicatedTitle,
            data:         duplicatedComponent, 
            content:      parseJsonField(data.content),
            collection:      parseJsonField(data.collection),
            meta: {
              ...(typeof data.meta === 'object' && !Array.isArray(data.meta) ? data.meta : {}),             
            },
          };
  
          // call createPage
          const response = await PagesServices.createPage(payload);
  
          // verfijn de teruggegeven nieuwe pagina
          const created = response.data.page;
          const normalized = {
            ...created,
            content: parseJsonField(created.content),
            data: {
              ...created.data,
              component: {
                ...parseJsonField(created.data.component),
                // parse de stappen als dat van toepassing is
                steps: parseJsonField(created.data.component?.steps)
              }
            }
          };
  
          // voeg het nieuwe item vooraan toe
          this.pages.unshift(normalized);
  
          // zet direct actief
          this.setActivePage(normalized);
          this.setActiveView(normalized);
          this.getPages();
  
        } catch (err) {
          console.error('Fout bij dupliceren van pagina:', err);
          this.error = 'Er is een fout opgetreden bij het dupliceren van de pagina.';
        } finally {
          this.loading = false;
        }
      },


      
      async setActivePage(page) {
        if (!page) return;
      
        if (page.meta) {
          page.meta = parseMeta(page.meta);
        }
      
        this.active.page = JSON.parse(JSON.stringify(page));      
        sessionStorage.setItem('lastActivePageId', page.id);
      
      },
      
    pushNavigation(ctx) {
      this.navigationStack.push(ctx);
    },
    // pop voor “back”
    popNavigation() {
      return this.navigationStack.pop();
    },

    setActiveFormScreen(page, show = true) {     
      this.active.form_screen = JSON.parse(JSON.stringify(page));
      this.show_active_form_screen = show;
    },


    async getUsers(params) {
      this.loading = true;
      this.error = null;

      try {
        const response = await DatasEditorServices.getCollectionData(
          2,
          params
        );
        this.users = response.data.data || [];
        if (this.users.length > 0) {
          this.active.user = this.users[0];          
        } 
      } catch (error) {
        this.error = "Er is een fout opgetreden bij het ophalen van de gebruikers.";
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async setActiveUser(user) {
      if (!user || !user.id) return;
    
      // Zet direct de gebruiker
      this.active.user = user;
    
      const userId = user.id;
    
      // Haal bijbehorende pagina's op
      await this.getPages();
    
      // Check of de gebruiker intussen veranderd is (race condition check)
      if (this.active.user.id !== userId) return;
    
      // Herlaad data en rollen als de gebruiker nog steeds actief is
      await this.getRoles();
    
      if (this.active.collection?.id) {
        await this.getData(this.active.collection.id, { page: 1 });
      }
    },
    
    
    async setActiveUserAndReloadAll(user) {
      if (!user || !user.id) {
        console.warn("Geen geldige gebruiker opgegeven aan setActiveUserAndReloadAll");
        return;
      }
    
      this.active.user = user;
    
      try {
        // Laad afhankelijk van de gebruiker alle relevante app data opnieuw in
        await Promise.all([
          this.getPages(),
          this.getRoles(),
          this.getCollections()
        ]);
    
        if (this.active.collection?.id) {
          await this.getFields(this.active.collection.id);
          console.log("Active Collection set to 3:", this.active.collection);
          await this.getData(this.active.collection.id, { page: 1 });
        }
    
      } catch (error) {
        console.error("Fout bij herladen van gegevens voor nieuwe gebruiker:", error);
      }
    },
    
    
    async setActiveUserProfilePage() {
      const page = this.pages.find(p => p.id === 3);
      if (!page) {
        console.warn("Pagina met ID 3 niet gevonden.");
        return;
      }
    
      // Zet de juiste actieve pagina's
      this.setActivePage(page);
      this.setActiveView(page);
      this.active_detail_page = { ...page };
    
      // Zoek de collectie met ID 2
      const collection = this.collections.find(c => c.collection.id === 2);
      if (!collection) {
        console.warn("Collectie met ID 2 niet gevonden.");
        return;
      }
    
      // Zet deze collectie als actief (roept automatisch getFields aan)
      this.setActiveCollection(collection);
    
 
    
      console.log("User Profile fields:", this.fields);
    },
     
       
    clearActivePage() {
      this.active.page = {};
    },
    updateActivePageField(key, value) {
      if (this.active.page) {
        this.active.page[key] = value;
      }
    },

    updateBuilder() {
      this.syncActiveWidgetToPage();
      this.updatePage();
    
    },
    

  

    debounceUpdatePage() {
      this._debouncedUpdatePage = debounce(() => this.updatePage(), 0);
      console.log("Debounced function initialized");
      console.log(this._debouncedUpdatePage);
    },
  
    debouncedUpdatePage() {
      if (this._debouncedUpdatePage) {
        this._debouncedUpdatePage();
      }
      this.updatePage();
    },
  
    cancelDebounce() {
      if (this._debouncedUpdatePage?.cancel) {
        this._debouncedUpdatePage.cancel();
      }
    },

    syncActiveWidgetToPage() {
      const page = this.active.page;
      const widget = this.active.widget;
    
      if (!page?.content || !widget?.id) return;
    
      const index = page.content.findIndex(w => w.id === widget.id);
      if (index !== -1) {
        this.active.page.content.splice(index, 1, JSON.parse(JSON.stringify(widget)));
      } else {
        console.warn("Kan widget niet synchroniseren: niet gevonden in page.content");
      }
    },    

    async updatePage() {
      if (!this.active.page?.id) return;
      this.updatingpage = true;
      this.is_saving_page = true;

    


      try {
        const response = await PagesServices.updatePage({ ...this.active.page });
        const newPage = response.data.data;

        
        if (newPage.meta) {
          newPage.meta = parseMeta(newPage.meta);
        }
    
       this.setActivePage(newPage);
       this.pages = response.data.pages.map(p => normalizePage(p));
        if(response.data.success){
          console.log(response.data.message);   
        } else {
          console.error(response.data.message);
        }

    
  
      } catch (error) {
        console.error("Error updating page:", error);
      } finally {
        this.is_saving_page = false;
        this.updatingpage = false;
      }
      if(this.show_active_form_screen){
        this.updateActiveFormPage();
      }

    },



    async updateActiveFormPage() {
      if (!this.active.form_screen?.id) return;
      this.updatingpage = true;
      this.is_saving_page = true;
      try {
        const response = await PagesServices.updatePage({ ...this.active.form_screen });
        const newPage = response.data.data;

        
        if (newPage.meta) {
          newPage.meta = parseMeta(newPage.meta);
        }
    
     
        this.setActivePage(newPage);
        if(response.data.success){
          console.log(response.data.message);
        } else {
          console.error(response.data.message);
        }
  
      } catch (error) {
        console.error("Error updating page:", error);
      } finally {
        this.is_saving_page = false;
        this.updatingpage = false;
      }
    },
    

    setAppPages(item) {
      this.pages = item;
    },

    // Views
    setActiveView(view) {
      if (view) {
        this.active.view = JSON.parse(JSON.stringify(view))
      }
    },    
    clearActiveView() {
      this.active.view = {};
    },
    updateActiveViewField(key, value) {
      if (this.active.view) {
        this.active.view[key] = value;
      }
    },

    // Widgets
    setActiveWidget(widget) {
      if (widget) {
        this.active.widget = widget; // 🔥 Laat Vue de reactiviteit beheren
        this.setActiveView(widget);
    
        if (widget?.collection?.id) {
          this.getFields(widget.collection.id).then(() => {
            console.log("Active Collection set to 2:", this.active.collection);
          });
        }
      }
    },

 
    clearActiveWidget() {
      this.active.widget = {};
    },
    updateActiveWidgetField(key, value) {
      if (this.active.widget) {
        this.active.widget[key] = value;
      }
    },

    async updateCollectionItem(itemId, updatedData) {
      try {
        const response = await DatasEditorServices.updateItem(itemId, updatedData);
        if (response.data.success) {
          console.log("Item updated successfully:", response.data.message);
          // Optionally update the collection data after updating an item
          await this.getCollectionData(this.active.collection.id, { page: 1 });
        } else {
          console.error("Failed to update item:", response.data.message);
        }
      } catch (error) {
        console.error("Error updating item:", error);
      }
    },


      // Make sure your collection data, field data, and relations are handled correctly
      async updateFieldsAfterCollectionChange() {
        if (this.active.collection) {
          console.log("Active Collection set to: 1", this.active.collection);
          await this.getFields(this.active.collection.id);  // Re-fetch fields if the collection changes
        }
      },

    // Collections
    setActiveCollection(item) {
      this.active.collection = item;    
      this.updateFieldsAfterCollectionChange();  
    },
    clearActiveCollection() {
      this.active.collection = {};
    },
    updateActiveCollectionField(key, value) {
      if (this.active.collection) {
        this.active.collection[key] = value;
      }
    },

      /****************************************************
     * Fields
     ****************************************************/
    setFields(fields) {
      this.fields = fields;
    },
    clearFields() {
      this.fields = [];
    },
    updateField(key, value) {
      const field = this.fields.find(f => f.key === key);
      if (field) {
        field.value = value;
      }
    },

    async getAllFields() {
      try {
        const response = await FieldsServices.getFields();

    
        // Zorg ervoor dat response.data.data een array is
        if (Array.isArray(response.data.data)) {
          this.all_fields = response.data.data.map((collection) => ({
            collectionName: collection.collection_name,
            collection: {
              id: collection.collection_id,
              name: collection.collection_name,
            },
            fields: collection.columns.map((col) => ({
              title: col.label,
              field: col.column_name,
              key: col.column_name,
              ...col,
            }))
          }));
        } else {
          console.error('De verwachte data is geen array:', response.data.data);
        }
        
      } catch (error) {
        console.error('Fout bij laden van velden voor collecties:', error);
      }
    },

    async setActiveCollectionById(collectionId) {
      const collection = this.collections.find(c => c.id === collectionId);
      if (!collection) {
        console.warn("Collectie niet gevonden voor ID:", collectionId);
        return;
      }
    
      this.active.collection = collection;
      console.log("Active Collection set to:", this.active.collection);
      await this.getFields(collectionId); // haalt en zet velden
    },
    

    async getFields(collection_id) {
      if (!collection_id) {
        console.warn("Geen collection_id opgegeven aan getFields");
        return;
      }
    
      console.log("Zoeken naar velden voor collectie ID:", collection_id);
    
      const match = this.all_fields.find(c =>
        c.collection.id === collection_id.collection || c.collection.id === collection_id
      );
    
      let fields = match ? match.fields : [];
    
      // ✅ Voeg gebruikersvelden toe als de actieve pagina een profielpagina is
      const isProfilePage = this.active.page?.screentype === "profile";
      if (isProfilePage) {
        const userFieldsGroup = this.all_fields.find(c => c.collection.id === 2);
        if (userFieldsGroup) {
          fields = [...fields, ...userFieldsGroup.fields];
          console.log("Gebruikersvelden toegevoegd aan profielpagina.");
        }
      }
    
      this.setFields(fields);
    
      if (!match) {
        console.warn("Geen matchende velden gevonden in all_fields");
      } else {
        console.log("Velden gevonden:", fields);
      }
    },
    
    
    
    
    /****************************************************
     * Items
     ****************************************************/
  async getData(collection_id,params) {
      this.loading = true;
      this.error = null;

      // if (this.active.user?.id && params) {
      //   params.user_id = this.active.user.id;
      // }

      try {
        const response = await DatasEditorServices.getCollectionData(
          collection_id,
          params
        );
       
  if (response && response.data) {
    this.data = response.data.data;
    this.pagination = response.data.pagination;
    
  } else {
    throw new Error("Ongeldige respons ontvangen");
  }
      } catch (error) {
        this.error = "Er is een fout opgetreden bij het ophalen van de lijsten.";
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
   getDetailPageById(state) {
    return (id) => {
      return state.active_detail_page.id === id ? state.active_detail_page : null;
    };
  },  
   setSelectedItem(item) {
    this.active_detail_page = item;
    console.log("Selected Collection active Item:", this.active_detail_page);
  },

  setActiveEditpage(Items) {
    this.active.edit_page = Items;
    console.log("Selected Collection edit page:", this.active.edit_page);
  },

   async getCollectionData() {
    this.loading = true;
    try {
      const response = await DatasEditorServices.getCollectionData(
        this.active.page.collection.id
      );
      this.fields_data = response.data.data;

      this.loading = false;
    } catch (error) {
      console.error("Failed to load fields data.");
      this.loading = false;
    }
  },

// In de actie getItemData in de store
async getItemData() {
  if (!this.active.page.collection.id || !this.active.page.id) {
    console.error("Collection ID or Item ID is missing.");
    return;
  }

  try {
    // Eerst de collectiedata ophalen
    await this.getCollectionData();

    // Zoek naar het specifieke item in de opgehaalde data
    const selectedItem = this.fields_data.find(
      (item) => item.id === this.active.page.id
    );
 

    if (selectedItem) {

      this.selected_item = selectedItem;  // Voeg dit toe aan je store als je het later wilt gebruiken
    } else {
      console.log("Item not found in collection.");
    }
  } catch (error) {
    console.error("Error fetching item data:", error);
  }
},


async getCollections() {
  this.loading = true;
  this.error = null;
  try {
    const response = await CollectionsServices.getCollections();
    this.collections = response.data.data || [];
    this.active.collection = this.collections[0] || {};
  } catch (error) {
    // this.$toast.error(error.data.message);
    this.collections = [];
  } finally {
    this.loading = false;
  }
},



    /****************************************************
     * Roles
     ****************************************************/
    setActiveRole(role) {
      this.active.role = role;
    },

    async getRoles() {
      try {
        const response = await RolesServices.getRoles();
        this.roles = response.data.data || [];
        this.active.role = this.roles[0] || {};
      } catch (error) {
        console.error("Fout bij ophalen van rollen:", error);
      }
    },

    async getRole(roleId) {
      try {
        const response = await RolesServices.getRole(roleId);
        this.active.role = response.data.data || {};
      } catch (error) {
        console.error("Fout bij ophalen van rol:", error);
      }
    },

    async createRole(data) {

      try {
        const response = await RolesServices.createRole(data);
        this.roles.push(response.data.data);
      } catch (error) {
        console.error("Fout bij aanmaken rol:", error);
      }

    },

    async getPermissionsForRole(roleId) {
      try {
        const res = await RolesServices.getPermissions(roleId);
        this.role_permissions = res.data.data || [];
        console.log("✅ Permissions geladen voor role:", roleId, this.role_permissions);
      } catch (err) {
        console.error("❌ Fout bij ophalen van permissions", err);
      }
    }
  },

  getters: {
 
    isOwnerOfApp: (state) => {
      return state.active.user?.id === state.active.app?.owner_id;
    },
    getActivePage: state => {
      return state.active.page;
    },
    getActiveApp: state => {
      return state.active.app;
    },
    getActiveWidget: state => {
      return state.active.widget;
    },

    getActiveCollection: state => {
      return state.active.collection;
    },

    getActiveView: state => {
      return state.active.view;
    },
    getFieldsList: state => {
      return state.fields;
    },

    getAllFieldsList: state => {
      return state.allFields;
    },
    getAppPages: state => {
      try {
        return state.pages;
      } catch (error) {
        console.error("Error fetching pages:", error);
        return [];
      }
    },
    getActiveAppId: state => {
      return state.active.app.id;
    },
    getActivePageId: state => {
      return state.active.page.id;
    },
  
    getActiveStep(state) {
      const steps = state.active.page?.data?.component?.steps || [];
      return steps[state.activeStepIndex] || null;
    },
  },
  persist: {
    storage: sessionStorage // wordt gewist bij sluiten tab
  }
});
